import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PenLine } from "lucide-react";
import { api } from "@/api";
import { PageHero, Reveal } from "@/components/bits";
import NewsletterSignup from "@/components/NewsletterSignup";

export function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function Insights() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data.articles))
      .catch(() => setArticles([]));
  }, []);

  return (
    <main data-testid="insights-page">
      <PageHero
        testid="insights-hero"
        eyebrow="Insights"
        title={
          <>
            Notes on nutrition, technology and <span className="text-tnn-purple">building for health.</span>
          </>
        }
        lead="Thinking from the TNN Africa team: what we are learning from building products, working with patients and partners, and exploring AI in nutrition."
      />

      <section className="bg-tnn-warm pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6">
          {articles === null ? (
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading…</p>
          ) : articles.length === 0 ? (
            <Reveal>
              <div className="bg-white border border-tnn-ink/8 rounded-3xl p-12 text-center" data-testid="insights-empty">
                <PenLine className="w-8 h-8 text-tnn-purple/40 mx-auto" />
                <p className="mt-4 font-display text-xl font-semibold text-tnn-ink">First pieces are on their way</p>
                <p className="mt-2 text-sm text-tnn-muted">We are writing. Check back soon.</p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={i * 0.05}>
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`article-card-${a.slug}`}
                    className="group flex flex-col h-full bg-white border border-tnn-ink/8 rounded-3xl overflow-hidden transition-[box-shadow,transform] duration-300 hover:shadow-[0_20px_50px_rgba(40,0,40,0.10)] hover:-translate-y-1"
                  >
                    {a.cover ? (
                      <div className="h-44 overflow-hidden border-b border-tnn-ink/6">
                        <img
                          src={a.cover}
                          alt={a.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      </div>
                    ) : (
                      <div className="h-44 bg-tnn-soft flex items-center justify-center border-b border-tnn-ink/6">
                        <PenLine className="w-7 h-7 text-tnn-purple/30" />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full bg-tnn-soft text-tnn-purple">
                          {a.topic}
                        </span>
                        <span className="text-xs text-tnn-muted">{formatDate(a.created_at)}</span>
                      </div>
                      <h2 className="mt-3 font-display text-lg font-semibold text-tnn-ink group-hover:text-tnn-purple transition-colors duration-200 leading-snug">
                        {a.title}
                      </h2>
                      {a.excerpt && (
                        <p className="mt-2 text-sm text-tnn-muted leading-relaxed line-clamp-3">{a.excerpt}</p>
                      )}
                      <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-xs font-bold text-tnn-purple">
                        Read <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
          <div className="max-w-2xl">
            <NewsletterSignup />
          </div>
        </div>
      </section>
    </main>
  );
}
